import { useEffect, useState } from "react";
import client, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Loader2, LogIn, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const localToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export const ShiftClockIn = ({ event }) => {
  const { user } = useAuth();
  const [inIds, setInIds] = useState(new Set());
  const [busy, setBusy] = useState(null);
  const isAdmin = user?.role === "owner" || user?.role === "admin";
  const show = isAdmin && event.kind === "shift" && event.date === localToday() && event.status !== "cancelled";
  const people = event.workers?.length ? event.workers : (event.workerId ? [{ id: event.workerId, name: event.workerName }] : []);

  useEffect(() => {
    if (!show) { return; }
    client.get("/attendance/clocked-in").then((r) => setInIds(new Set((r.data?.workers || []).map((w) => w.workerId)))).catch(() => {});
  }, [show, event.id]);

  if (!show || people.length === 0) return null;

  const clockIn = async (w) => {
    setBusy(w.id);
    try {
      await client.post("/attendance/admin/start", { workerId: w.id, siteId: event.siteId });
      toast.success(`${w.name} clocked in`);
      setInIds((s) => new Set([...s, w.id]));
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(null); }
  };

  return (
    <div className="space-y-1.5 rounded-lg border border-emerald-200 bg-emerald-50/50 p-3" data-testid="shift-clockin">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Clock in</p>
      {people.map((w) => (
        <div key={w.id} className="flex items-center justify-between gap-2">
          <span className="text-sm text-slate-800 truncate">{w.name}</span>
          {inIds.has(w.id) ? (
            <span className="flex items-center gap-1 text-xs font-semibold text-emerald-700" data-testid={`shift-clockin-done-${w.id}`}><CheckCircle2 className="h-3.5 w-3.5" />Clocked in</span>
          ) : (
            <Button size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-700" disabled={busy === w.id} onClick={() => clockIn(w)} data-testid={`shift-clockin-btn-${w.id}`}>
              {busy === w.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><LogIn className="h-3.5 w-3.5 mr-1" />Clock in</>}
            </Button>
          )}
        </div>
      ))}
    </div>
  );
};
